import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Divider,
  Autocomplete,
  TextField,
  Chip,
  Card,
  CardContent,
  Button,
  Skeleton,
  Paper,
  // For The Filter 
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import axios from "axios";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import RoomIcon from "@mui/icons-material/Room";
import SearchIcon from "@mui/icons-material/Search";




const normalizeBaseUrl = (raw) => {
  const s = String(raw || "").trim().replace(/\/+$/, "");
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `http://${s}`;
};


const viewerEmpId = localStorage.getItem("shared_empid") || "default";
const FILTERS_KEY = `allEmployeesActions_filters_${viewerEmpId}`;

const loadSavedFilters = () => {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

//based on action of employee the statment will be  
function emptyVisitMessage(action) {
  if (!action || action.type === "none") return "No status reported";
  if (action.type === "attendance") {
    return action.label === "In Company"
      ? " currently in company"
      : " already left for the day";
  }
  if (action.type === "status") {
    return ` reported ${action.label.toLowerCase()}`;
  }
  if (action.type === "leave") {
    return `  ${action.label.toLowerCase()}`;
  }
  return "No status reported";
}

// Visual treatment per current-action type/label.
function actionChipProps(action) {
  if (!action) return { label: "—", color: "default", borderColor: "#bdbdbd" };

  if (action.type === "leave") {
    return { label: action.label, color: "secondary", borderColor: "#7b1fa2" };
  }
  if (action.type === "visit") {
    return { label: action.label, color: "info", borderColor: "#0288d1" };
  }
  if (action.type === "status") {
    const byLabel = {
      Late: { color: "warning", borderColor: "#f57c00" },
      Accident: { color: "error", borderColor: "#d32f2f" },
      "Visit before company": { color: "info", borderColor: "#0288d1" },
      Other: { color: "default", borderColor: "#9e9e9e" },
    };
    const match = byLabel[action.label] || { color: "default", borderColor: "#9e9e9e" };
    return { label: action.label, ...match };
  }
  if (action.type === "attendance") {
    return action.label === "In Company"
      ? { label: action.label, color: "info", borderColor: "#0288d1" }
      : { label: action.label, color: "success", borderColor: "#2e7d32" };
  }
  return { label: "—", color: "default", borderColor: "#bdbdbd" };
}
// To Filter Employee Status 
const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All"},
  { value: "in_company", label: "In Company"},
  { value: "out", label: "Company Out"},
  { value: "visit", label: "Visit"},
  { value: "leave", label: "Leave"},
  { value: "employee_status", label: "Employee Status"},
  { value: "none", label: "None"},
];
// Maps a row's currentAction to one of the filter buckets above.
function matchesStatusFilter(action, filterValue) {
  if (filterValue === "all") return true;

  const type = action?.type;
  if (filterValue === "none") return !type || type === "none";
  if (filterValue === "leave") return type === "leave";
  if (filterValue === "visit") return type === "visit";
  if (filterValue === "in_company") return type === "attendance" && action.label === "In Company";
   if (filterValue === "out") return type === "attendance" && action.label !== "In Company";
  // covers Late, Visit before company, Accident, Other — all the STATUS_LABELS values from the backend
  if (filterValue === "employee_status") return type === "status";

  return true;
}

export default function AllEmployeesActions() {
  const API_BASE_URL = useMemo(
    () => normalizeBaseUrl(localStorage.getItem("saved_sett_api_url")),
    []
  );

  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null); // null = all 
  
  const [statusFilter, setStatusFilter]= useState("all"); 

  const [month, setMonth] = useState(() => {
    const saved = loadSavedFilters();
    if (saved?.month && saved?.year) {
      return dayjs(`${saved.year}-${String(saved.month).padStart(2, "0")}-01`);
    }
    return dayjs();
  });
  const pendingEmpIdRef = useRef(loadSavedFilters()?.empId ?? null);

 

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

   // employees with no status/action at all are always hidden on this page
  // const visibleRows = rows.filter((r) => r.currentAction?.type !== "none");
  const visibleRows = rows.filter((r) => matchesStatusFilter(r.currentAction, statusFilter));

  // ── load employee list once, for the filter dropdown ──
  // /api/employees -> employees_list() -> { data: [ {empId, fullName}, ... ] }
  useEffect(() => {
    if (!API_BASE_URL) return;
    axios
      .get(`${API_BASE_URL}/api/employees`)
      .then((res) => {
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setEmployees(list);

        // restore the previously-selected employee, now that we have the full list to match against
        if (pendingEmpIdRef.current != null) {
          const match = list.find((e) => e.empId === pendingEmpIdRef.current);
          if (match) setSelectedEmp(match);
          pendingEmpIdRef.current = null;
        }
      })
      .catch((err) => console.error("Failed to load employees:", err));
  }, [API_BASE_URL]);

  const fetchActions = useCallback(async () => {
    if (!API_BASE_URL) {
      setError("API base URL is not set in settings.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    const params = {
      month: month.month() + 1, // dayjs months are 0-indexed
      year: month.year(),
    };
    if (selectedEmp) params.empId = selectedEmp.empId;

    // remember this filter combo so it survives a page refresh
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify({
        empId: selectedEmp ? selectedEmp.empId : null,
        month: params.month,
        year: params.year,
      }));
    } catch (e) {
      console.error("Failed to save filters:", e);
    }

    try {
      const res = await axios.get(`${API_BASE_URL}/api/employee-actions`, { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load employee actions:", err);
      setError(err?.response?.data?.error || "Failed to load employee actions.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, month, selectedEmp]);

  // initial load only - further loads happen when "Apply" is clicked
 // initial load, and auto-refetch whenever the employee filter changes
 // If we're about to restore a saved employee filter, skip this first
  // unfiltered fetch entirely - wait for selectedEmp to update once the
  // employees list loads and matches it, then this effect fires again
  // with the correct filter already in place.
  useEffect(() => {
    if (pendingEmpIdRef.current !=null) return; 
    fetchActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmp]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
        <Avatar sx={{ bgcolor: "#00838f" }}>
          <GroupIcon />
        </Avatar>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "#004d40" }}>
          All Employees Status
        </Typography>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* ── Filters ── */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2.5 }} alignItems={{ sm: "center" }}>
        <Autocomplete
          options={employees}
          getOptionLabel={(opt) => opt.fullName || ""}
          isOptionEqualToValue={(opt, val) => opt.empId === val.empId}
          value={selectedEmp}
          onChange={(_, val) => setSelectedEmp(val)}
          sx={{ minWidth: { xs: "100%", sm: 260 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search employee"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />,
              }}
            />
          )}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Month"
            views={["year", "month"]}
            value={month}
            onChange={(val) => val && setMonth(val)}
            slotProps={{ textField: { size: "small", sx: { minWidth: { xs: "100%", sm: 180 } } } }}
          />
        </LocalizationProvider>

       {/* Filter Employee status None , all, in company .. */}
        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))} 
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={fetchActions}
          sx={{ bgcolor: "#00796b", fontWeight: 700, "&:hover": { bgcolor: "#004d40" } }}
        >
          Apply
        </Button>
       
      </Stack>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
    
      {/* ── Cards ── */}
      {loading ? (
        <Stack spacing={2}>
          {[...Array(4)].map((_, i) => (
            <Card key={i} elevation={4} sx={{ p: 2 }}>
              <Skeleton variant="rectangular" height={18} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={18} width="60%" />
            </Card>
          ))}
        </Stack>
      ) : visibleRows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderStyle: "dashed", bgcolor: "#fafafa" }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            No employees found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try a different employee or month filter.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {visibleRows.map((row) => {
            const chip = actionChipProps(row.currentAction);
            const visit = row.lastVisit;
            return (
              <Card
                key={row.empId}
                elevation={4}
                sx={{
                  borderRadius: 3,
                  borderLeft: `4px solid ${chip.borderColor}`,
                  background: "linear-gradient(180deg, #ffffff 0%, #f8fffe 100%)",
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PersonIcon sx={{ color: "#00796b" }} />
                      <Typography variant="subtitle1" fontWeight={700}>
                        {row.fullName}
                      </Typography>
                    </Stack>
                    <Chip
                      label={chip.label}
                      color={chip.color}
                      size="small"
                      variant={chip.color === "default" ? "outlined" : "filled"}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>

                  {row.currentAction?.note && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      {row.currentAction.note}
                    </Typography>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <Stack direction="row" spacing={1} alignItems="center">
                    <RoomIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    {visit ? (
                    <Typography variant="body2">
                      {visit.visitFrom} → {visit.visitTo}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({visit.date}, {visit.timeIn}{visit.timeOut ? `–${visit.timeOut}` : ""})
                      </Typography>
                    </Typography>
                  ) : row.currentAction?.type === "leave" && (row.currentAction.timeIn || row.currentAction.timeOut) ? (
                    <Typography variant="body2" color="text.secondary">
                      {emptyVisitMessage(row.currentAction)}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({row.currentAction.timeIn || "—"}{row.currentAction.timeOut ? ` – ${row.currentAction.timeOut}` : ""})
                      </Typography>
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {emptyVisitMessage(row.currentAction)}
                    </Typography>
                  )}
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}